import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  berlinDate,
  NOTICE_ANCHOR_LABELS,
  NOTICE_ANCHORS,
  noticeEffectiveDate,
  TERMINATION_FORM_LABELS,
  TERMINATION_HINTS,
  TERMINATION_KIND_LABELS,
  TERMINATION_KINDS,
  type Contract,
  type ContractTermination,
  type NoticeRule,
  type TerminationForm,
  type TerminationInitiator,
  type TerminationKind,
} from '@kontor/shared';
import { api, ApiError } from '../../api';
import './contracts.css';

const dayFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
export const deDate = (iso: string) => dayFormat.format(new Date(`${iso}T12:00:00Z`));

/**
 * Maske für eine Kündigung, neu oder als Entwurf ändern. Wirkia berechnet aus dem Tag des Zugangs und der Frist den Beendigungstermin und zeigt ihn gleich an. Bei Arbeitsverträgen ist die
 * Schriftform vorgegeben, denn eine elektronische Kündigung wäre dort unwirksam (§ 623 BGB). Den Text des Schreibens bearbeitet man danach auf der Seite der Kündigung.
 */
export function TerminationFormPage() {
  const { id = '', tid } = useParams();
  const editing = !!tid;
  const navigate = useNavigate();
  const client = useQueryClient();

  const contract = useQuery({ queryKey: ['contract', id], queryFn: async () => (await api<{ data: Contract }>('GET', `/contracts/${id}`)).data });
  const existing = contract.data?.terminations.find((t) => t.id === tid);

  const [initiatedBy, setInitiatedBy] = useState<TerminationInitiator>('company');
  const [kind, setKind] = useState<TerminationKind>('ordinary');
  const [form, setForm] = useState<TerminationForm>('written');
  const [planned, setPlanned] = useState(berlinDate());
  const [reason, setReason] = useState('');
  const [probation, setProbation] = useState(false);
  const [employedSince, setEmployedSince] = useState('');
  const [custom, setCustom] = useState(false);
  const [customValue, setCustomValue] = useState(4);
  const [customUnit, setCustomUnit] = useState<'days' | 'weeks' | 'months'>('weeks');
  const [customAnchor, setCustomAnchor] = useState<(typeof NOTICE_ANCHORS)[number]>('month_end');
  const [chosen, setChosen] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);

  const employment = contract.data?.kind === 'employment';

  useEffect(() => {
    if (!existing) return;
    setInitiatedBy(existing.initiatedBy);
    setKind(existing.kind);
    setForm(existing.form);
    setPlanned(existing.plannedDeliveryDate);
    setReason(existing.reason ?? '');
    setProbation(existing.rule.probation);
    setEmployedSince(existing.rule.employedSince ?? '');
    if (existing.rule.custom) {
      setCustom(true);
      setCustomValue(existing.rule.custom.value);
      setCustomUnit(existing.rule.custom.unit);
      setCustomAnchor(existing.rule.custom.anchor);
    }
  }, [existing]);

  // Arbeitsverträge und Schreiben des Vertragspartners sind immer Schriftform
  useEffect(() => {
    if (employment || initiatedBy === 'party') setForm('written');
  }, [employment, initiatedBy]);

  const rule: NoticeRule = useMemo(
    () => ({ by: initiatedBy === 'company' ? 'employer' : 'employee', probation: employment ? probation : false, employedSince: employment && employedSince ? employedSince : null, custom: custom ? { value: customValue, unit: customUnit, anchor: customAnchor } : null }),
    [initiatedBy, employment, probation, employedSince, custom, customValue, customUnit, customAnchor],
  );
  const preview = useMemo(() => (planned ? noticeEffectiveDate(planned, rule, employment) : null), [planned, rule, employment]);

  const save = useMutation({
    mutationFn: () => {
      const payload = { initiatedBy, kind, form, reason: reason.trim() || null, plannedDeliveryDate: planned, rule, effectiveDate: kind === 'ordinary' && chosen ? chosen : null };
      return editing ? api<{ data: ContractTermination }>('PATCH', `/contracts/${id}/terminations/${tid}`, payload) : api<{ data: ContractTermination }>('POST', `/contracts/${id}/terminations`, payload);
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['contract', id] });
      client.invalidateQueries({ queryKey: ['contracts'] });
      navigate(`/vertraege/${id}/kuendigung/${res.data.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFailure(null);
      } else setFailure(e instanceof ApiError ? e.message : 'Das Speichern hat nicht geklappt.');
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    setFailure(null);
    if (kind === 'extraordinary' && reason.trim().length < 10) return setErrors({ reason: 'Eine außerordentliche Kündigung braucht einen wichtigen Grund. Bitte beschreiben Sie ihn.' });
    save.mutate();
  };

  if (contract.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!contract.data) return <div className="card narrow"><h1>Vertrag nicht gefunden</h1><Link className="btn btn--secondary" to="/vertraege">Zurück zu den Verträgen</Link></div>;
  const c = contract.data;
  if (c.status !== 'signed') return <div className="card narrow"><h1>Kündigung nicht möglich</h1><p className="muted">Gekündigt werden kann nur ein unterschriebener Vertrag.</p><Link className="btn btn--secondary" to={`/vertraege/${id}`}>Zurück zum Vertrag</Link></div>;
  if (editing && existing && existing.status !== 'draft') return <div className="card narrow"><h1>Kündigung nicht änderbar</h1><p className="muted">Eine freigegebene Kündigung lässt sich nicht mehr ändern.</p><Link className="btn btn--secondary" to={`/vertraege/${id}/kuendigung/${tid}`}>Zurück zur Kündigung</Link></div>;

  const hints = employment ? TERMINATION_HINTS.employment : TERMINATION_HINTS.other;
  const field = (name: string) => errors[name];
  const party = initiatedBy === 'party';

  return (
    <>
      <p><Link to={`/vertraege/${id}`} className="back-link">← Zurück zum Vertrag</Link></p>
      <div className="page-head"><h1>{editing ? 'Kündigung bearbeiten' : 'Kündigung erfassen'}</h1></div>
      <p className="muted">{c.title} · {c.number} · {c.partyName}</p>

      <div className="alert alert--warn" role="note">
        <strong>Bitte beachten</strong>
        <ul className="small">{hints.map((h) => <li key={h}>{h}</li>)}</ul>
      </div>

      <form className="card contract-form" onSubmit={submit} noValidate>
        {failure && <div className="alert alert--error" role="alert">{failure}</div>}

        <div className="form-grid">
          <label className="field">
            <span>Wer kündigt?</span>
            <select className="input" value={initiatedBy} onChange={(e) => setInitiatedBy(e.target.value as TerminationInitiator)}>
              <option value="company">Wir kündigen</option>
              <option value="party">{c.partyName} hat gekündigt (Eingang erfassen)</option>
            </select>
          </label>
          <label className="field">
            <span>Art der Kündigung</span>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as TerminationKind)}>
              {TERMINATION_KINDS.map((k) => <option key={k} value={k}>{TERMINATION_KIND_LABELS[k]}</option>)}
            </select>
          </label>
        </div>

        <label className="field">
          <span>Form</span>
          <select className="input" value={form} disabled={employment || party} onChange={(e) => setForm(e.target.value as TerminationForm)}>
            <option value="written">{TERMINATION_FORM_LABELS.written}</option>
            {!employment && !party && <option value="electronic">{TERMINATION_FORM_LABELS.electronic}</option>}
          </select>
          {field('form') && <span className="field__error" role="alert">{field('form')}</span>}
          {employment && <span className="muted small">Ein Arbeitsverhältnis lässt sich nur schriftlich kündigen. Wirkia erzeugt das Schreiben zum Ausdrucken und hält danach den Zugang mit dem Scan fest.</span>}
          {!employment && !party && form === 'electronic' && <span className="muted small">Prüfen Sie im Vertrag, ob er eine elektronische Kündigung zulässt. Verlangt er Schriftform, wählen Sie die Schriftform.</span>}
        </label>

        <label className="field">
          <span>{party ? 'Das Schreiben ist eingegangen am' : 'Die Kündigung soll zugehen am'}</span>
          <input className="input" type="date" value={planned} onChange={(e) => setPlanned(e.target.value)} style={{ maxWidth: '12rem' }} aria-invalid={!!field('plannedDeliveryDate')} />
          {field('plannedDeliveryDate') && <span className="field__error" role="alert">{field('plannedDeliveryDate')}</span>}
          <span className="muted small">Die Frist beginnt mit dem Zugang, nicht mit dem Datum des Schreibens. Je später es zugeht, desto später endet der Vertrag.</span>
        </label>

        {employment && (
          <div className="form-grid">
            <label className="check">
              <input type="checkbox" checked={probation} onChange={(e) => setProbation(e.target.checked)} />
              <span>Das Arbeitsverhältnis ist noch in der Probezeit (höchstens sechs Monate)</span>
            </label>
            {!probation && !party && (
              <label className="field">
                <span>Beschäftigt seit (für die Frist nach Betriebszugehörigkeit)</span>
                <input className="input" type="date" value={employedSince} onChange={(e) => setEmployedSince(e.target.value)} style={{ maxWidth: '12rem' }} />
              </label>
            )}
          </div>
        )}

        <label className="check">
          <input type="checkbox" checked={custom} onChange={(e) => setCustom(e.target.checked)} />
          <span>Im Vertrag ist eine eigene Kündigungsfrist vereinbart</span>
        </label>
        {custom && (
          <div className="form-grid">
            <label className="field">
              <span>Frist</span>
              <input className="input" type="number" min={0} max={120} value={customValue} onChange={(e) => setCustomValue(Number(e.target.value))} style={{ maxWidth: '8rem' }} />
            </label>
            <label className="field">
              <span>Einheit</span>
              <select className="input" value={customUnit} onChange={(e) => setCustomUnit(e.target.value as 'days' | 'weeks' | 'months')}>
                <option value="days">Tage</option>
                <option value="weeks">Wochen</option>
                <option value="months">Monate</option>
              </select>
            </label>
            <label className="field">
              <span>Termin</span>
              <select className="input" value={customAnchor} onChange={(e) => setCustomAnchor(e.target.value as (typeof NOTICE_ANCHORS)[number])}>
                {NOTICE_ANCHORS.map((a) => <option key={a} value={a}>{NOTICE_ANCHOR_LABELS[a]}</option>)}
              </select>
            </label>
          </div>
        )}

        {kind === 'ordinary' && preview && (
          <div className="alert alert--ok" role="status">
            <strong>Frühester Beendigungstermin: {deDate(preview.date)}</strong>
            <div className="small">{preview.label}. Die Berechnung kennt weder Tarifverträge noch Sonderfälle und ist eine Hilfe, keine Rechtsberatung.</div>
          </div>
        )}
        {kind === 'extraordinary' && (
          <div className="alert alert--warn" role="note">
            Die fristlose Kündigung wirkt mit dem Zugang. Sie muss innerhalb von zwei Wochen nach Kenntnis des Grundes zugehen (§ 626 Abs. 2 BGB). Das Schreiben enthält vorsorglich die ordentliche Kündigung{preview ? ` zum ${deDate(preview.date)}` : ''}.
          </div>
        )}

        {kind === 'ordinary' && !party && (
          <label className="field">
            <span>Anderer Beendigungstermin (freiwillig)</span>
            <input className="input" type="date" value={chosen} onChange={(e) => setChosen(e.target.value)} style={{ maxWidth: '12rem' }} aria-invalid={!!field('effectiveDate')} />
            {field('effectiveDate') && <span className="field__error" role="alert">{field('effectiveDate')}</span>}
            <span className="muted small">Nur ein späterer Termin als der berechnete ist möglich. Ohne Angabe gilt der berechnete.</span>
          </label>
        )}

        <label className="field">
          <span>{kind === 'extraordinary' ? 'Wichtiger Grund' : 'Grund (freiwillig, bei Kündigungsschutz oft nötig)'}</span>
          <textarea className="input" style={{ minHeight: '6rem' }} value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} aria-invalid={!!field('reason')} />
          {field('reason') && <span className="field__error" role="alert">{field('reason')}</span>}
        </label>

        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : editing ? 'Speichern' : party ? 'Weiter zum Eingang' : 'Entwurf anlegen'}</button>
          <Link className="btn btn--secondary" to={editing ? `/vertraege/${id}/kuendigung/${tid}` : `/vertraege/${id}`}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
